import React, { FC } from 'react';
import { Procedure as ProcedureT } from '../../../../../../types/Procedure';
import { Anesthesiologist, Physician } from '../../anesthesiologist/Personnel';
import { Bed, PatientNameWithSession } from '../../Patient';
import { PatientStat } from '../../shared/PatientStat';
import { useStatusColor } from '@/hooks/useStatusColor';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { formatDuration, shouldHighlightPhysicianInOr } from '../../shared/procedureUtils';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';

const Label = ({ text, backgroundColor }: { text: string; backgroundColor: string }) => (
  <Box borderRadius={4} bgcolor={backgroundColor} style={{ fontWeight: 'bold', color: 'white' }} px={0.5}>
    {text}
  </Box>
);

const CardCompactHeader: FC<{
  procedure: ProcedureT;
  physicianName?: string;
  anesthesiologistName?: string;
  anesthesiaType?: string;
  color: string;
  status: string;
  showBedNumber: boolean;
  inOrStatus: string;
  roomTime: any;
  hospitalId?: number;
  isKiosk?: boolean;
  timelineViewRoomTime?: boolean | null;
  fullHeight?: boolean;
  isFlipRoom?: boolean;
}> = ({
  procedure,
  physicianName,
  anesthesiologistName,
  anesthesiaType,
  color,
  status,
  showBedNumber,
  inOrStatus,
  roomTime,
  hospitalId,
  isKiosk,
  timelineViewRoomTime,
  fullHeight,
  isFlipRoom,
}) => {
  const classes = useStyles();
  const getColor = useStatusColor('schedule');

  const { compactInitials, wrapProcedureName } = useScreenConfiguration();

  const highlightPhysician = shouldHighlightPhysicianInOr({
    orId: procedure?.orId,
    physicianCurrentRoomId: procedure?.physician?.currentRoomId,
    patientRoomId: procedure?.patient?.room?.id,
    status,
    requirePatientInOr: true,
  });

  return (
    <Box className={classes.header}>
      <Box className={classes.row}>
        <Physician physicianName={physicianName} color={color} inline highlight={highlightPhysician} />
        <Box className={classes.info}>
          <Anesthesiologist
            procedureId={procedure.id}
            anesthesiologistName={anesthesiologistName}
            anesthesiaType={anesthesiaType}
            inline
            style={{ fontWeight: 400 }}
          />
          {!wrapProcedureName && !fullHeight && (
            <PatientStat inline right>
              {!!roomTime &&
                (timelineViewRoomTime || !isKiosk) &&
                formatDuration(roomTime, new Date(), false, {
                  fontSize: '0.75rem',
                  fontFamily:
                    'Rubik,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
                })}{' '}
              {isFlipRoom && <Label backgroundColor="#6c9911" text="FR" />}
              <Label backgroundColor={getColor(status, hospitalId) || ''} text={inOrStatus ? inOrStatus : status} />
              {showBedNumber && !!procedure?.patient?.bed && <Bed bed={procedure?.patient?.bed} />}
            </PatientStat>
          )}
        </Box>
      </Box>
      <Box className={clsx(classes.secondRow, { [classes.secondRowAlignStart]: wrapProcedureName || fullHeight })}>
        <PatientNameWithSession
          {...{
            id: procedure?.patient?.id ?? procedure?.patientId,
            name: isKiosk
              ? compactInitials
                ? toCompactInitials(procedure?.patient?.name || procedure?.patientName)
                : procedure?.patient?.name || procedure?.patientName
              : procedure?.patient?.name
                  ?.split(' ')
                  .map(n => n[0])
                  .join('.') ||
                procedure?.patientName
                  ?.split(' ')
                  .map(n => n[0])
                  .join(''),
            age: procedure?.patientAge,
            sex: procedure?.patientSex,
            hospitalId,
            inline: true,
            isPatientIconVisible: false,
            condensView: false,
            isTimeline: true,
            compactInitials,
            scheduleIsKiosk: isKiosk,
          }}
        />
        {!wrapProcedureName && !fullHeight ? (
          <Box className={clsx(classes.procedure)}>{procedure?.procedureType?.replaceAll('\n', ' ')}</Box>
        ) : (
          <PatientStat inline right>
            {!!roomTime &&
              timelineViewRoomTime &&
              formatDuration(roomTime, new Date(), false, {
                fontSize: '0.75rem',
                fontFamily:
                  'Rubik,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
              })}{' '}
            {isFlipRoom && <Label backgroundColor="#6c9911" text="FR" />}
            <Label backgroundColor={getColor(status, hospitalId) || ''} text={inOrStatus ? inOrStatus : status} />
            {showBedNumber && !!procedure?.patient?.bed && <Bed bed={procedure?.patient?.bed} />}
          </PatientStat>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    lineHeight: 'normal',
    paddingLeft: '0.375em',
    height: '100%',
    gap: '0.25em',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& > *': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
  },
  secondRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  secondRowAlignStart: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  procedure: {
    overflow: 'hidden',
    display: 'block',
    whiteSpace: 'nowrap',
  },
}));

export default CardCompactHeader;

const toCompactInitials = (name: string | undefined): string | undefined =>
  name ? name[0] + name.replaceAll('.', '').replaceAll(' ', '').slice(-1) : '';
